import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Badge, ButtonGroup, Button as BSButton, Spinner, Alert, ProgressBar } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import { useSelector, useDispatch } from 'react-redux';
import { fetchPrestatairesStats, fetchPeriodStats } from '../../../store/actions/RapportAction';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from "react-i18next";

const StatisticsDashboard = ({ dateRange, refreshTrigger, viewMode }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dt = useRef(null);
  
  // Récupérer les données du store Redux avec une structure par défaut pour éviter les undefined
  const { 
    prestataires = { data: { data: [] }, loading: false, error: null }, 
    periodStats = { data: { data: [] }, periodType: 'mensuel', loading: false, error: null }
  } = useSelector(state => state.rapport || {});
  
  // États pour la table PrimeReact
  const [prestataireData, setPrestataireData] = useState([]);
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.EQUALS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    consultations: { value: null, matchMode: FilterMatchMode.EQUALS },
    factures: { value: null, matchMode: FilterMatchMode.EQUALS }
  });
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState(1);
  const [selectedPrestataires, setSelectedPrestataires] = useState([]);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [periodType, setPeriodType] = useState('mensuel');
  
  // Charger les données au chargement du composant et lors des changements de période
  useEffect(() => {
    dispatch(fetchPrestatairesStats({
      dateDebut: dateRange.startDate,
      dateFin: dateRange.endDate
    }));
    dispatch(fetchPeriodStats(periodType, {
      dateDebut: dateRange.startDate,
      dateFin: dateRange.endDate
    }));
  }, [dispatch, dateRange, periodType, refreshTrigger]);
  
  // Mettre à jour les données locales lorsque les données Redux changent
  useEffect(() => {
    const data = prestataires?.data?.data || [];
    setPrestataireData(data);
  }, [prestataires]);
  
  // Gérer le changement de période
  const handlePeriodChange = (newPeriodType) => {
    setPeriodType(newPeriodType);
  };
  
  // S'assurer que les données existent avant de les utiliser
  const periodData = periodStats?.data?.data || [];
  
  // Nombre total de prestataires uniques
  const uniqueProviders = prestataireData.length;
  
  // Calculer les totaux pour le tableau des prestataires
  const totalConsultations = prestataireData.reduce(
    (sum, item) => sum + (item?.consultations || 0), 0
  );
  
  const totalInvoices = prestataireData.reduce(
    (sum, item) => sum + (item?.factures || 0), 0
  );
  
  // Calculer la moyenne des consultations par prestataire
  const avgConsultations = uniqueProviders > 0 
    ? Math.round((totalConsultations / uniqueProviders) * 10) / 10 
    : 0;
  
  // Calculer la moyenne des factures par prestataire
  const avgInvoices = uniqueProviders > 0 
    ? Math.round((totalInvoices / uniqueProviders) * 10) / 10 
    : 0;
  
  // Trouver le prestataire le plus actif
  const mostActiveProvider = prestataireData.length > 0 
    ? prestataireData.reduce((prev, current) => 
        (prev.consultations > current.consultations) ? prev : current
      ) 
    : null;
  
  // Créer des catégories sécurisées pour le graphique
  const safeCategories = periodData.map(item => item?.period || '');
  
  // Créer des données sécurisées pour les séries du graphique
  const safeConsultationsData = periodData.map(item => item?.consultations || 0);
  const safeProvidersData = periodData.map(item => item?.activeProviders || 0);
  const safeInvoicesData = periodData.map(item => item?.invoices || 0);
  
  // Configuration du graphique principal
  const mainChartOptions = {
    chart: {
      type: 'area',
      height: 400,
      fontFamily: 'inherit',
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      },
      background: 'transparent',
      stacked: false
    },
    colors: ['#3CB371', '#4682B4', '#FF7F50'],
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: [3, 3, 3],
      dashArray: [0, 0, 0]
    },
    fill: {
      type: 'gradient',
      gradient: {
        opacityFrom: 0.7,
        opacityTo: 0.2,
        shade: 'light',
        type: 'vertical',
        stops: [0, 90, 100]
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      offsetY: -10,
      fontSize: '13px',
      markers: {
        width: 12,
        height: 12,
        radius: 6
      }
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    xaxis: {
      categories: safeCategories,
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: 'inherit'
        }
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      }
    },
    yaxis: [
      {
        title: {
          text: 'Consultations',
          style: {
            fontSize: '13px',
            fontWeight: 500
          }
        },
        labels: {
          style: {
            fontSize: '12px'
          },
          formatter: (value) => Math.round(value)
        }
      },
      {
        opposite: true,
        title: {
          text: 'Prestataires / Factures',
          style: {
            fontSize: '13px',
            fontWeight: 500
          }
        },
        labels: {
          style: {
            fontSize: '12px'
          },
          formatter: (value) => Math.round(value)
        }
      }
    ],
    tooltip: {
      shared: true,
      intersect: false,
      style: {
        fontSize: '12px',
        fontFamily: 'inherit'
      },
      theme: 'light',
      y: {
        formatter: function (val, { seriesIndex }) {
          if (seriesIndex === 0) return (val || 0) + " consultations";
          if (seriesIndex === 1) return (val || 0) + " prestataires";
          return (val || 0) + " factures";
        }
      }
    },
    markers: {
      size: 4,
      strokeWidth: 0,
      hover: {
        size: 6
      }
    },
    responsive: [
      {
        breakpoint: 576,
        options: {
          yaxis: {
            show: false
          },
          legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            offsetY: 0
          }
        }
      }
    ]
  };

  // Séries pour le graphique combiné
  const mainChartSeries = [
    {
      name: 'Consultations',
      data: safeConsultationsData
    },
    {
      name: 'Prestataires actifs',
      data: safeProvidersData
    },
    {
      name: 'Factures',
      data: safeInvoicesData
    }
  ];

  // Données pour le graphique en donut de répartition
  const donutChartOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'inherit',
      toolbar: {
        show: false
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    colors: ['#4682B4', '#3CB371', '#FF7F50'],
    labels: ['Prestataires actifs', 'Consultations', 'Factures'],
    plotOptions: {
      pie: {
        donut: {
          size: '55%',
          labels: {
            show: true,
            total: {
              show: true,
              showAlways: true,
              label: 'Total',
              fontSize: '16px',
              fontWeight: 600,
              color: '#495057',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 700,
              color: '#495057'
            }
          }
        }
      }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '13px',
      markers: {
        width: 10,
        height: 10,
        radius: 5
      }
    },
    tooltip: {
      shared: false,
      y: {
        formatter: function (val) {
          return val;
        }
      }
    }
  };

  const donutChartSeries = [
    periodData.reduce((sum, item) => sum + (item?.activeProviders || 0), 0),
    periodData.reduce((sum, item) => sum + (item?.consultations || 0), 0),
    periodData.reduce((sum, item) => sum + (item?.invoices || 0), 0)
  ];

  // Fonctions pour le gestionnaire de filtre global
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    
    _filters['global'].value = value;
    
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const clearFilter = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.EQUALS },
      name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
      consultations: { value: null, matchMode: FilterMatchMode.EQUALS },
      factures: { value: null, matchMode: FilterMatchMode.EQUALS }
    });
    setGlobalFilterValue('');
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const exportExcel = () => {
    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(prestataireData);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      
      saveAsExcelFile(excelBuffer, 'prestataires');
    });
  };

  const saveAsExcelFile = (buffer, fileName) => {
    import('file-saver').then((module) => {
      if (module && module.default) {
        let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        let EXCEL_EXTENSION = '.xlsx';
        const data = new Blob([buffer], { type: EXCEL_TYPE });
        
        module.default.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
      }
    });
  };

  // Modèles de cellules pour DataTable
  const idBodyTemplate = (rowData) => {
    return (
      <span className="font-weight-bold text-secondary">{rowData.id}</span>
    );
  };

  const nameBodyTemplate = (rowData) => {
    return (
      <div className="provider-name d-flex align-items-center">
        <div className="provider-avatar mr-2">
          <div className="avatar-circle" style={{
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            background: '#f0f8ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4682B4',
            fontWeight: 'bold',
            fontSize: '16px'
          }}>
            {rowData.name.charAt(0).toUpperCase()}
          </div>
        </div>
        <div className="provider-info">
          <div className="provider-name font-weight-medium">{rowData.name}</div>
          {showAllColumns && (
            <div className="provider-id text-muted" style={{ fontSize: '0.8rem' }}>ID: {rowData.id}</div>
          )}
        </div>
      </div>
    );
  };

  const consultationsBodyTemplate = (rowData) => {
    const percentage = Math.min(100, Math.round((rowData.consultations / (mostActiveProvider?.consultations || 1)) * 100));
    
    return (
      <div className="d-flex align-items-center">
        <span className="mr-2 font-weight-medium">{rowData.consultations}</span>
        <div style={{ flex: 1, maxWidth: '100px' }}>
          <ProgressBar 
            now={percentage} 
            variant="success" 
            style={{ height: '6px', borderRadius: '3px' }} 
          />
        </div>
      </div>
    );
  };

  const facturesBodyTemplate = (rowData) => {
    // Trouver le prestataire avec le plus de factures
    const maxFactures = prestataireData.reduce((max, item) => Math.max(max, item.factures || 0), 0);
    const percentage = Math.min(100, Math.round((rowData.factures / (maxFactures || 1)) * 100));
    
    return (
      <div className="d-flex align-items-center">
        <span className="mr-2 font-weight-medium">{rowData.factures}</span>
        <div style={{ flex: 1, maxWidth: '100px' }}>
          <ProgressBar 
            now={percentage} 
            variant="warning" 
            style={{ height: '6px', borderRadius: '3px' }} 
          />
        </div>
      </div>
    );
  };

  // Modèle d'en-tête pour le filtre global
  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <Button 
            type="button" 
            icon="pi pi-filter-slash" 
            label="Effacer" 
            outlined 
            onClick={clearFilter} 
            className="mr-2"
            size="small" 
          />
          <Button 
            type="button" 
            icon="pi pi-file-excel" 
            label="Excel" 
            severity="success" 
            onClick={exportExcel} 
            className="mr-2"
            size="small"
          />
          <Button 
            type="button" 
            icon="pi pi-file" 
            label="CSV" 
            severity="info" 
            onClick={exportCSV}
            size="small"
          />
          <Button
            type="button"
            icon={showAllColumns ? "pi pi-columns" : "pi pi-table"}
            label={showAllColumns ? "Vue simple" : "Détails"}
            severity="secondary"
            outlined
            onClick={() => setShowAllColumns(!showAllColumns)}
            className="ml-2"
            size="small"
          />
        </div>
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText 
            value={globalFilterValue} 
            onChange={onGlobalFilterChange} 
            placeholder="Rechercher..." 
            style={{ fontSize: '0.9rem' }}
          />
        </span>
      </div>
    );
  };

  const header = renderHeader();

  return (
    <div className={`statistics-dashboard ${viewMode === 'compact' ? 'compact-view' : ''}`}>
      {/* Cartes de statistiques */}
      <Row className="mb-4">
    <Col lg={3} md={6} sm={12} className="mb-3">
        <Card className="h-100 border-0 card-stats" style={{ 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            transition: 'transform 0.3s ease'
        }}>
            <Card.Body style={{ 
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e1f5fe 100%)',
                padding: '25px 20px',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    right: '20px',
                    top: '20px',
                    fontSize: '2.5rem',
                    opacity: '0.15',
                    color: '#4682B4'
                }}>
                    <i className="fa fa-users"></i>
                </div>
                <div style={{ position: 'relative', zIndex: '2' }}>
                    <div style={{ 
                        fontSize: '0.95rem', 
                        marginBottom: '12px',
                        color: '#6c757d',
                        fontWeight: '500'
                    }}>Prestataires uniques</div>
                    <div style={{ 
                        fontSize: '2.2rem', 
                        fontWeight: '700',
                        marginBottom: '15px',
                        color: '#4682B4'
                    }}>{uniqueProviders}</div>
                    <div style={{
                        fontSize: '0.9rem',
                        color: '#28a745',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <i className="fa fa-check-circle mr-2"></i> Actifs sur la période
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
    
    <Col lg={3} md={6} sm={12} className="mb-3">
        <Card className="h-100 border-0 card-stats" style={{ 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            transition: 'transform 0.3s ease'
        }}>
            <Card.Body style={{ 
                background: 'linear-gradient(135deg, #f0fff4 0%, #e3fcec 100%)',
                padding: '25px 20px',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    right: '20px',
                    top: '20px',
                    fontSize: '2.5rem',
                    opacity: '0.15',
                    color: '#3CB371'
                }}>
                    <i className="fa fa-stethoscope"></i>
                </div>
                <div style={{ position: 'relative', zIndex: '2' }}>
                    <div style={{ 
                        fontSize: '0.95rem', 
                        marginBottom: '12px',
                        color: '#6c757d',
                        fontWeight: '500'
                    }}>Consultations</div>
                    <div style={{ 
                        fontSize: '2.2rem', 
                        fontWeight: '700',
                        marginBottom: '15px',
                        color: '#3CB371'
                    }}>{totalConsultations}</div>
                    <div style={{
                        fontSize: '0.9rem',
                        color: '#6c757d',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <i className="fa fa-calculator mr-2"></i> {avgConsultations} en moyenne
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
    
    <Col lg={3} md={6} sm={12} className="mb-3">
        <Card className="h-100 border-0 card-stats" style={{ 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            transition: 'transform 0.3s ease'
        }}>
            <Card.Body style={{ 
                background: 'linear-gradient(135deg, #fffbf0 0%, #fff8e1 100%)',
                padding: '25px 20px',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    right: '20px',
                    top: '20px',
                    fontSize: '2.5rem',
                    opacity: '0.15',
                    color: '#ffc107'
                }}>
                    <i className="fa fa-file-invoice"></i>
                </div>
                <div style={{ position: 'relative', zIndex: '2' }}>
                    <div style={{ 
                        fontSize: '0.95rem', 
                        marginBottom: '12px',
                        color: '#6c757d',
                        fontWeight: '500'
                    }}>Factures traitées</div>
                    <div style={{ 
                        fontSize: '2.2rem', 
                        fontWeight: '700',
                        marginBottom: '15px',
                        color: '#ffc107'
                    }}>{totalInvoices}</div>
                    <div style={{
                        fontSize: '0.9rem',
                        color: '#6c757d',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <i className="fa fa-calculator mr-2"></i> {avgInvoices} en moyenne
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
    
    <Col lg={3} md={6} sm={12} className="mb-3">
        <Card className="h-100 border-0 card-stats" style={{ 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            transition: 'transform 0.3s ease'
        }}>
            <Card.Body style={{ 
                background: 'linear-gradient(135deg, #fff5f5 0%, #ffe6e6 100%)',
                padding: '25px 20px',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    right: '20px',
                    top: '20px',
                    fontSize: '2.5rem',
                    opacity: '0.15',
                    color: '#dc3545'
                }}>
                    <i className="fa fa-trophy"></i>
                </div>
                <div style={{ position: 'relative', zIndex: '2' }}>
                    <div style={{ 
                        fontSize: '0.95rem', 
                        marginBottom: '12px',
                        color: '#6c757d',
                        fontWeight: '500'
                    }}>Prestataire plus actif</div>
                    <div style={{ 
                        fontSize: '1.4rem', 
                        fontWeight: '700',
                        marginBottom: '15px',
                        color: '#dc3545',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>{mostActiveProvider?.name?.substring(0, 22) || 'N/A'}</div>
                    <div style={{
                        fontSize: '0.9rem',
                        color: '#6c757d',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <i className="fa fa-chart-line mr-2"></i> {mostActiveProvider?.consultations || 0} consultations
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
</Row>



      {/* Graphique principal */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
              <div>
                <h5 className="card-title mb-0 text-primary">Évolution des statistiques</h5>
                <small className="text-muted">Tendances d'utilisation sur la période sélectionnée</small>
              </div>
              <ButtonGroup size="sm">
                <BSButton 
                  variant={periodType === 'mensuel' ? 'primary' : 'outline-primary'} 
                  onClick={() => handlePeriodChange('mensuel')}
                >
                  Mensuel
                </BSButton>
                <BSButton 
                  variant={periodType === 'hebdomadaire' ? 'primary' : 'outline-primary'} 
                  onClick={() => handlePeriodChange('hebdomadaire')}
                >
                  Hebdomadaire
                </BSButton>
                <BSButton 
                  variant={periodType === 'aujourd\'hui' ? 'primary' : 'outline-primary'} 
                  onClick={() => handlePeriodChange('aujourd\'hui')}
                >
                  Aujourd'hui
                </BSButton>
              </ButtonGroup>
            </Card.Header>
            <Card.Body className="p-0 pb-3">
              {periodStats.loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Chargement des statistiques...</p>
                </div>
              ) : periodStats.error ? (
                <Alert variant="danger" className="m-3">
                  <i className="fa fa-exclamation-circle mr-2"></i>
                  Erreur lors du chargement des statistiques: {periodStats.error}
                </Alert>
              ) : periodData.length === 0 ? (
                <Alert variant="info" className="m-3">
                  <i className="fa fa-info-circle mr-2"></i>
                  Aucune donnée statistique disponible pour cette période.
                </Alert>
              ) : (
                <div id="combinedChart" className="px-2 pt-3">
                  <ReactApexChart
                    options={mainChartOptions}
                    series={mainChartSeries}
                    type="area"
                    height={400}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Statistiques détaillées */}
      <Row className="mb-4">
        <Col lg={4} md={12} className="mb-3 mb-lg-0">
          <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-white">
              <h5 className="card-title mb-0 text-primary">Répartition des données</h5>
            </Card.Header>
            <Card.Body className="chart-container">
              {periodStats.loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" size="sm" />
                </div>
              ) : (
                <ReactApexChart
                  options={donutChartOptions}
                  series={donutChartSeries}
                  type="donut"
                  height={320}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8} md={12}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-white">
              <h5 className="card-title mb-0 text-primary">Statistiques clés</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0 text-muted font-weight-medium" style={{ width: '40%' }}>Métrique</th>
                      <th className="border-0 text-muted font-weight-medium" style={{ width: '30%' }}>Valeur</th>
                      <th className="border-0 text-muted font-weight-medium" style={{ width: '30%' }}>Tendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="metric-icon bg-primary-light p-2 rounded-circle mr-2">
                            <i className="fa fa-users text-primary"></i>
                          </div>
                          <span className="font-weight-medium">Prestataires actifs</span>
                        </div>
                      </td>
                      <td className="font-weight-bold">{uniqueProviders}</td>
                      <td>
                        {periodData.length > 1 && (
                          <div className="d-flex align-items-center">
                            {safeProvidersData[safeProvidersData.length - 1] > safeProvidersData[0] ? (
                              <>
                                <i className="fa fa-arrow-up text-success mr-1"></i>
                                <span className="text-success font-weight-medium">
                                  {Math.round(((safeProvidersData[safeProvidersData.length - 1] - safeProvidersData[0]) / (safeProvidersData[0] || 1)) * 100)}%
                                </span>
                              </>
                            ) : (
                              <>
                                <i className="fa fa-arrow-down text-danger mr-1"></i>
                                <span className="text-danger font-weight-medium">
                                  {Math.abs(Math.round(((safeProvidersData[safeProvidersData.length - 1] - safeProvidersData[0]) / (safeProvidersData[0] || 1)) * 100))}%
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="metric-icon bg-success-light p-2 rounded-circle mr-2">
                            <i className="fa fa-stethoscope text-success"></i>
                          </div>
                          <span className="font-weight-medium">Total consultations</span>
                        </div>
                      </td>
                      <td className="font-weight-bold">{totalConsultations}</td>
                      <td>
                        {periodData.length > 1 && (
                          <div className="d-flex align-items-center">
                            {safeConsultationsData[safeConsultationsData.length - 1] > safeConsultationsData[0] ? (
                              <>
                                <i className="fa fa-arrow-up text-success mr-1"></i>
                                <span className="text-success font-weight-medium">
                                  {Math.round(((safeConsultationsData[safeConsultationsData.length - 1] - safeConsultationsData[0]) / (safeConsultationsData[0] || 1)) * 100)}%
                                </span>
                              </>
                            ) : (
                              <>
                                <i className="fa fa-arrow-down text-danger mr-1"></i>
                                <span className="text-danger font-weight-medium">
                                  {Math.abs(Math.round(((safeConsultationsData[safeConsultationsData.length - 1] - safeConsultationsData[0]) / (safeConsultationsData[0] || 1)) * 100))}%
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="metric-icon bg-warning-light p-2 rounded-circle mr-2">
                            <i className="fa fa-file-invoice text-warning"></i>
                          </div>
                          <span className="font-weight-medium">Total factures</span>
                        </div>
                      </td>
                      <td className="font-weight-bold">{totalInvoices}</td>
                      <td>
                        {periodData.length > 1 && (
                          <div className="d-flex align-items-center">
                            {safeInvoicesData[safeInvoicesData.length - 1] > safeInvoicesData[0] ? (
                              <>
                                <i className="fa fa-arrow-up text-success mr-1"></i>
                                <span className="text-success font-weight-medium">
                                  {Math.round(((safeInvoicesData[safeInvoicesData.length - 1] - safeInvoicesData[0]) / (safeInvoicesData[0] || 1)) * 100)}%
                                </span>
                              </>
                            ) : (
                              <>
                                <i className="fa fa-arrow-down text-danger mr-1"></i>
                                <span className="text-danger font-weight-medium">
                                  {Math.abs(Math.round(((safeInvoicesData[safeInvoicesData.length - 1] - safeInvoicesData[0]) / (safeInvoicesData[0] || 1)) * 100))}%
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="metric-icon bg-info-light p-2 rounded-circle mr-2">
                            <i className="fa fa-calculator text-info"></i>
                          </div>
                          <span className="font-weight-medium">Consultations / Prestataire</span>
                        </div>
                      </td>
                      <td className="font-weight-bold">{avgConsultations}</td>
                      <td>
                        <Badge variant="info" className="p-2">
                          Moyenne
                        </Badge>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tableau des prestataires */}
      <Row>
        <Col lg={12}>
          <Card className="shadow-sm border-0">
            <Card.Header className="d-flex justify-content-between align-items-center bg-white">
              <div>
                <h5 className="card-title mb-0 text-primary">Liste des prestataires</h5>
                <small className="text-muted">Détails d'utilisation par prestataire</small>
              </div>
              <Badge bg="primary" pill className="px-3 py-2">
                {uniqueProviders} prestataires différents ont utilisé A-CARE
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {prestataires.loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Chargement des données prestataires...</p>
                </div>
              ) : prestataires.error ? (
                <Alert variant="danger" className="m-3">
                  <i className="fa fa-exclamation-circle mr-2"></i>
                  Erreur lors du chargement des prestataires: {prestataires.error}
                </Alert>
              ) : prestataireData.length === 0 ? (
                <Alert variant="info" className="m-3">
                  <i className="fa fa-info-circle mr-2"></i>
                  Aucune donnée de prestataire disponible.
                </Alert>
              ) : (
                <div className="data-table-container">
                  <DataTable 
                    ref={dt}
                    value={prestataireData} 
                    paginator 
                    rows={10} 
                    rowsPerPageOptions={[5, 10, 25, 50]} 
                    dataKey="id"
                    filters={filters} 
                    filterDisplay="menu" 
                    globalFilterFields={['id', 'name', 'consultations', 'factures']} 
                    header={header}
                    emptyMessage="Aucun prestataire trouvé."
                    currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} prestataires"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    selectionMode="multiple"
                    selection={selectedPrestataires}
                    onSelectionChange={(e) => setSelectedPrestataires(e.value)}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={(e) => {
                      setSortField(e.sortField);
                      setSortOrder(e.sortOrder);
                    }}
                    responsiveLayout="scroll"
                    stripedRows
                    showGridlines={false}
                    rowHover
                    className="custom-datatable"
                  >
                    <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                    {showAllColumns && (
                      <Column field="id" header="ID" body={idBodyTemplate} sortable style={{ width: '8%' }} />
                    )}
                    <Column field="name" header="Nom du prestataire" body={nameBodyTemplate} sortable style={{ minWidth: showAllColumns ? '40%' : '50%' }} />
                    <Column field="consultations" header="Consultations" body={consultationsBodyTemplate} sortable style={{ minWidth: '25%' }} />
                    <Column field="factures" header="Factures traitées" body={facturesBodyTemplate} sortable style={{ minWidth: '25%' }} />
                  </DataTable>
                  
                  <div className="card-footer bg-white p-3 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong className="text-muted">Total:</strong>
                      </div>
                      <div className="d-flex">
                        <div className="mx-3 text-center">
                          <h5 className="text-success mb-0">{totalConsultations}</h5>
                          <small className="text-muted">consultations</small>
                        </div>
                        <div className="mx-3 text-center">
                          <h5 className="text-warning mb-0">{totalInvoices}</h5>
                          <small className="text-muted">factures</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StatisticsDashboard;